import { BACKEND_URL } from '../config';
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Leaf, ArrowLeft, Languages, LogOut } from 'lucide-react';

const ScanHistory = ({ token, language, onLogout, onToggleLanguage }) => {
  const navigate = useNavigate();
  const [scans, setScans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedScan, setSelectedScan] = useState(null);

  useEffect(() => {
    fetchScans();
  }, []);

  const fetchScans = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/scans`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      if (response.ok) {
        const data = await response.json();
        setScans(data);
      }
    } catch (err) {
      if (process.env.NODE_ENV === 'development') console.error('Error fetching scans:', err);
    } finally {
      setLoading(false);
    }
  };

  const translations = {
    en: {
      title: 'Scan History',
      back: 'Back to Dashboard',
      noScans: 'No scans yet. Start scanning to see your history!',
      disease: 'Disease',
      description: 'Description',
      treatment: 'Treatment',
      prevention: 'Prevention',
      severity: 'Severity',
      date: 'Date',
      viewDetails: 'View Details'
    },
    hi: {
      title: 'स्कैन इतिहास',
      back: 'डैशबोर्ड पर वापस जाएं',
      noScans: 'अभी तक कोई स्कैन नहीं। अपना इतिहास देखने के लिए स्कैन करना शुरू करें!',
      disease: 'रोग',
      description: 'विवरण',
      treatment: 'उपचार',
      prevention: 'रोकथाम',
      severity: 'गंभीरता',
      date: 'तारीख',
      viewDetails: 'विवरण देखें'
    }
  };

  const t = translations[language];

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="history-page">
      {/* Header */}
      <header className="app-header">
        <div className="header-content">
          <div className="logo">
            <Leaf size={32} />
            <span>BoAI</span>
          </div>
          <div className="header-actions">
            <button className="lang-toggle" onClick={onToggleLanguage}>
              <Languages size={16} /> {language.toUpperCase()}
            </button>
            <button className="icon-btn" onClick={onLogout}>
              <LogOut size={20} />
            </button>
          </div>
        </div>
      </header>

      <div className="history-content">
        <div className="container">
          <div className="history-header">
            <button className="btn-secondary" onClick={() => navigate('/dashboard')}>
              <ArrowLeft size={18} /> {t.back}
            </button>
            <h2>{t.title}</h2>
          </div>

          {loading ? (
            <div className="loading">Loading...</div>
          ) : scans.length === 0 ? (
            <div className="no-data">
              <Leaf size={64} />
              <p>{t.noScans}</p>
            </div>
          ) : (
            <div className="scans-grid">
              {scans.map((scan) => (
                <div key={scan.id} className="scan-card card">
                  <div className="scan-header">
                    <h3>
                      {language === 'en' ? scan.disease_name_en : scan.disease_name_hi}
                    </h3>
                    <div className={`severity-badge ${scan.severity}`}>
                      {scan.severity}
                    </div>
                  </div>
                  <p className="scan-date">{formatDate(scan.scan_date)}</p>
                  <p className="scan-description">
                    {language === 'en' ? scan.description_en : scan.description_hi}
                  </p>
                  <button
                    className="btn-secondary"
                    onClick={() => setSelectedScan(scan)}
                  >
                    {t.viewDetails}
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Detail Modal */}
      {selectedScan && (
        <div className="modal-overlay" onClick={() => setSelectedScan(null)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>
                {language === 'en' ? selectedScan.disease_name_en : selectedScan.disease_name_hi}
              </h2>
              <button className="close-btn" onClick={() => setSelectedScan(null)}>×</button>
            </div>
            <div className="modal-body">
              <div className="result-item">
                <h4>{t.severity}</h4>
                <div className={`severity-badge ${selectedScan.severity}`}>
                  {selectedScan.severity}
                </div>
              </div>
              <div className="result-item">
                <h4>{t.description}</h4>
                <p>{language === 'en' ? selectedScan.description_en : selectedScan.description_hi}</p>
              </div>
              <div className="result-item">
                <h4>{t.treatment}</h4>
                <p>{language === 'en' ? selectedScan.treatment_en : selectedScan.treatment_hi}</p>
              </div>
              {selectedScan.prevention_en && (
                <div className="result-item">
                  <h4>{t.prevention}</h4>
                  <p>{language === 'en' ? selectedScan.prevention_en : selectedScan.prevention_hi}</p>
                </div>
              )}
              <div className="result-item">
                <h4>{t.date}</h4>
                <p>{formatDate(selectedScan.scan_date)}</p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ScanHistory;
